/**
 * 67.57: the squad leader, round two (the owner, Oct 18: "Honestly current squad leader is fine just the weird helmet
 * thing it has I don't like. I think helmet changes could be more nuanced across squad leader halment and normal
 * helmet. I think it probably makes sense to align with armor system so light helment, heavy helment, squad leader
 * variant for both.").
 *
 * `dressLeaderKit` is 68.8's leader (personModel.ts `addLeader`) without its helmet pieces — the stripe over the top and
 * the chevron on the back are gone. What stays is on the torso: the radio on the pack's top and its whip on the support
 * side, the data slate on the chest rig, the handset on the support strap, the binocular case at the support hip. The
 * leader's helmet is now the leader's variant of whichever helmet is worn (armourLooks.ts `dressArmour`, from `PersonKit.leader`).
 *
 * `dressBodyPass` (for anyone): knee pads, elbow pads, cargo pockets on the thighs, a dump pouch at the back of the
 * belt, a canteen on the support hip — detail that breaks up the limbs' long boxes up close.
 *
 * Everything goes on groups that already move, so it bakes into their draws: no new draws. Rendering only.
 */
import * as THREE from 'three';
import { markShared } from './meshBake';
import type { PersonModel } from './personModel';
import type { UnitPalette } from './unitPalette';

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}
function bar(parent: THREE.Object3D, a: [number, number, number], b: [number, number, number], thick: number, color: string): THREE.Mesh {
  const va = new THREE.Vector3(...a), vb = new THREE.Vector3(...b), d = vb.clone().sub(va);
  const m = new THREE.Mesh(new THREE.BoxGeometry(thick, d.length() + thick * 0.5, thick), mat(color));
  m.position.copy(va).addScaledVector(d, 0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
  parent.add(m);
  return m;
}
function count(o: THREE.Object3D): number { let n = 0; o.traverse(q => { if ((q as THREE.Mesh).isMesh) n++; }); return n; }

/** 68.8's leader kit on the torso, without its helmet pieces. Returns the parts added. */
export function dressLeaderKit(m: PersonModel, p: UnitPalette): number {
  const t = m.torso, before = count(m.full);
  // the radio and its antenna
  box(t, 0.22, 0.13, 0.15, p.dark, -0.08, 0.625, -0.27);
  box(t, 0.05, 0.04, 0.05, p.metal, -0.15, 0.71, -0.3);
  bar(t, [-0.15, 0.72, -0.3], [-0.21, 1.48, -0.42], 0.022, p.dark).userData.part = 'antenna';
  box(t, 0.04, 0.04, 0.04, p.dark, -0.21, 1.48, -0.42);
  // the slate on the chest rig, the handset on the support strap, binoculars at the support hip
  box(t, 0.17, 0.12, 0.025, p.metal, 0.06, 0.34, 0.19);
  box(t, 0.13, 0.08, 0.01, p.flash, 0.06, 0.345, 0.205);
  box(t, 0.05, 0.11, 0.04, p.metal, -0.15, 0.5, 0.19);
  box(t, 0.08, 0.13, 0.13, p.dark, -0.29, 0.03, 0.02);
  return count(m.full) - before;
}

/**
 * The body pass, on any person: knee pads on the shins, elbow pads on the forearms, cargo pockets on the thighs, a dump
 * pouch at the back of the belt, a canteen on the support hip. Returns the parts added (no new draws).
 */
export function dressBodyPass(m: PersonModel, p: UnitPalette): number {
  const before = count(m.full);
  for (const shin of [m.shinL, m.shinR]) { box(shin, 0.21, 0.13, 0.08, p.dark, 0, -0.03, 0.1); box(shin, 0.22, 0.025, 0.07, p.metal, 0, 0.03, 0.1); }
  for (const arm of [m.armL, m.armR]) box(arm.fore, 0.16, 0.1, 0.1, p.dark, 0, -0.02, -0.07);
  for (const [thigh, out] of [[m.thighL, Math.sign(m.thighL.position.x)], [m.thighR, Math.sign(m.thighR.position.x)]] as const) {
    box(thigh, 0.04, 0.15, 0.16, p.dark, out * 0.115, -0.24, 0.01);
    box(thigh, 0.045, 0.03, 0.165, p.dark, out * 0.115, -0.15, 0.01);
  }
  box(m.torso, 0.24, 0.13, 0.1, p.dark, 0.06, -0.02, -0.2);   // the dump pouch
  const canteen = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.16, 6), mat(p.dark)); canteen.position.set(-0.28, -0.02, -0.1); m.torso.add(canteen);
  box(m.torso, 0.05, 0.025, 0.05, p.metal, -0.28, 0.075, -0.1);
  return count(m.full) - before;
}
